import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import { getContractStatus, getDaysRemaining, getExpiryLabel, isLicensedContract, shouldShowDaysRemaining, shouldShowWarning } from "@/utils/contractHelpers";
import { CONTRACT_STATUS, CONTRACT_TYPE_CONFIG, CONTRACT_TYPES } from "@/constants/contracts";
import { useLicensesByContract } from "@/hooks/useLicenses";
import { usePermissions } from "@/hooks/usePermissions";
import { DetailField } from "@/components/ui/DetailField";
import { ContractPricingDisplay } from "./ContractPricingDisplay";
import { LicenseBar } from "./LicenseBar";
import { LicenseHistory } from "./LicenseHistory";
import { getPricingDisplay } from "./pricingDisplay";
const FEATURE_LABELS = {
    ipsec: "IPsec",
    dqkd: "DQKD",
    blast: "BLAST",
    eaas: "EaaS"
};
const STATUS_DOT = {
    active: "bg-green-500",
    expired: "bg-red-500",
    pending: "bg-yellow-500"
};
const STATUS_TEXT = {
    active: "text-green-700",
    expired: "text-red-600",
    pending: "text-yellow-700"
};
export function ContractCard({ contractDetails }) {
    const { contract, usagePlan, pricingPolicy } = contractDetails;
    const status = getContractStatus(contract.ExpiryDate);
    const pricing = getPricingDisplay(contract, pricingPolicy);
    const daysRemaining = getDaysRemaining(contract.ExpiryDate);
    const contractType = contract.ContractType ?? CONTRACT_TYPES.DIRECT;
    const licensed = isLicensedContract(contract);
    const apiKeysLimit = contract.ApiKeysLimit ?? usagePlan?.ApiKeysLimit;
    const displayName = contract.Name?.replace(/^undefined\s*-\s*/i, "").trim();
    const { canViewLicenses, canGenerateLicense } = usePermissions();
    const typeLabel = CONTRACT_TYPE_CONFIG[contractType].label;
    const { data: licensesData, isLoading: isLoadingLicenses } = useLicensesByContract(contract.ContractId, {
        enabled: licensed && canViewLicenses
    });
    const activeLicense = licensesData?.licenses.find((l)=>l.Status === "ACTIVE") ?? null;
    const isExpired = status === CONTRACT_STATUS.EXPIRED;
    // Build subtitle parts
    const subtitleParts = [
        typeLabel,
        licensed ? "Licensed" : "Metered"
    ].filter(Boolean);
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx("rounded-lg bg-white border border-gray-200", isExpired && "opacity-70"),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "px-6 pt-5 pb-4",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-start justify-between",
                        children: /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-base font-semibold text-gray-900",
                                            children: displayName || "Contract"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "inline-flex items-center gap-1",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: clsx("h-2 w-2 rounded-full", STATUS_DOT[status] ?? "bg-gray-400")
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: clsx("text-xs font-medium", STATUS_TEXT[status] ?? "text-gray-500"),
                                                    children: status === CONTRACT_STATUS.ACTIVE ? "Active" : status === CONTRACT_STATUS.EXPIRED ? "Expired" : "Pending"
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-0.5 text-sm text-gray-500",
                                    children: subtitleParts.join(" \u00B7 ")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-4 grid grid-cols-2 gap-x-12 lg:grid-cols-4",
                        children: [
                            /*#__PURE__*/ _jsx(DetailField, {
                                label: "Created",
                                className: "py-1.5",
                                children: new Date(contract.CreatedAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(DetailField, {
                                label: getExpiryLabel(contractType),
                                className: "py-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: isExpired ? "text-red-600 font-medium" : "",
                                        children: new Date(contract.ExpiryDate).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric"
                                        })
                                    }),
                                    shouldShowDaysRemaining(contractType, status) && /*#__PURE__*/ _jsxs("span", {
                                        className: clsx("ml-1 text-xs", shouldShowWarning(contractType, daysRemaining) ? "text-red-600 font-semibold" : "text-gray-400"),
                                        children: [
                                            "(",
                                            daysRemaining,
                                            "d)"
                                        ]
                                    })
                                ]
                            }),
                            licensed && contract.license_terms ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Max Devices",
                                        className: "py-1.5",
                                        children: contract.license_terms.max_devices
                                    }),
                                    /*#__PURE__*/ _jsx(DetailField, {
                                        label: "SLA",
                                        className: "py-1.5",
                                        children: contract.SLA || "—"
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Pricing",
                                        className: "py-1.5",
                                        children: /*#__PURE__*/ _jsx(ContractPricingDisplay, {
                                            pricing: pricing
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(DetailField, {
                                        label: "Rate Limit",
                                        className: "py-1.5",
                                        children: [
                                            usagePlan?.RateLimit || 0,
                                            " calls/sec"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    licensed && contract.license_terms && /*#__PURE__*/ _jsx("div", {
                        className: "mt-3 flex gap-3",
                        children: Object.entries(contract.license_terms.features).map(([feature, count])=>/*#__PURE__*/ _jsxs("span", {
                                className: clsx("inline-flex items-center gap-1.5 rounded bg-gray-100 px-2.5 py-1 text-xs font-medium", count > 0 ? "text-gray-700" : "text-gray-400"),
                                children: [
                                    FEATURE_LABELS[feature] || feature,
                                    " ",
                                    /*#__PURE__*/ _jsx("span", {
                                        className: count > 0 ? "text-gray-400" : "",
                                        children: count
                                    })
                                ]
                            }, feature))
                    })
                ]
            }),
            licensed && canViewLicenses && !isLoadingLicenses && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-gray-100 px-6 py-3 bg-gray-50/50",
                children: activeLicense || canGenerateLicense ? /*#__PURE__*/ _jsx(LicenseBar, {
                    contractId: contract.ContractId,
                    license: activeLicense,
                    customerId: contract.CustomerId
                }) : null
            }),
            licensed && canViewLicenses && isLoadingLicenses && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-gray-100 px-6 py-3 text-xs text-gray-400",
                children: "Loading license data..."
            }),
            /*#__PURE__*/ _jsxs("details", {
                className: "group",
                children: [
                    /*#__PURE__*/ _jsxs("summary", {
                        className: "flex items-center justify-center gap-1.5 px-6 py-2 cursor-pointer list-none text-xs font-medium text-gray-400 hover:text-gray-600 border-t border-gray-100 [&::-webkit-details-marker]:hidden select-none",
                        children: [
                            /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3.5 w-3.5 transition-transform group-open:rotate-180"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "group-open:hidden",
                                children: "More details"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "hidden group-open:inline",
                                children: "Less"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 pb-5 pt-1 border-t border-gray-50",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-2 gap-x-12 lg:grid-cols-4 gap-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Customer ID",
                                        mono: true,
                                        children: contract.CustomerId
                                    }),
                                    /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Plan ID",
                                        mono: true,
                                        children: contract.PlanId || "Not applicable"
                                    }),
                                    contract.StartDate && /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Start Date",
                                        children: new Date(contract.StartDate).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric"
                                        })
                                    }),
                                    contract.BillingStartDate && /*#__PURE__*/ _jsx(DetailField, {
                                        label: "Billing Start",
                                        children: new Date(contract.BillingStartDate).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            year: "numeric"
                                        })
                                    }),
                                    contract.SLA && !licensed && /*#__PURE__*/ _jsx(DetailField, {
                                        label: "SLA",
                                        children: contract.SLA
                                    })
                                ]
                            }),
                            !licensed && usagePlan && /*#__PURE__*/ _jsx("div", {
                                className: "mt-4 pt-3 border-t border-gray-100",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-x-12 lg:grid-cols-4 gap-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(DetailField, {
                                            label: "API Keys Limit",
                                            children: apiKeysLimit ?? "Unlimited"
                                        }),
                                        /*#__PURE__*/ _jsxs(DetailField, {
                                            label: "Burst Limit",
                                            children: [
                                                usagePlan.BurstLimit || 0,
                                                " calls"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(DetailField, {
                                            label: "Quota",
                                            children: [
                                                usagePlan.QuotaValue?.toLocaleString() || "—",
                                                " ",
                                                usagePlan.QuotaUnits || "",
                                                " / ",
                                                usagePlan.QuotaPeriod || "—"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(DetailField, {
                                            label: "Plan Name",
                                            children: usagePlan.Name || contract.PlanId
                                        })
                                    ]
                                })
                            }),
                            contract.Metadata && Object.keys(contract.Metadata).length > 0 && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-4 pt-3 border-t border-gray-100",
                                children: [
                                    /*#__PURE__*/ _jsx("h5", {
                                        className: "text-xs font-medium text-gray-500 mb-2",
                                        children: "Metadata"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "grid grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-1",
                                        children: Object.entries(contract.Metadata).map(([key, value])=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex justify-between text-sm py-0.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-gray-500",
                                                        children: key
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-gray-900",
                                                        children: String(value)
                                                    })
                                                ]
                                            }, key))
                                    })
                                ]
                            }),
                            licensed && canViewLicenses && licensesData && licensesData.licenses.length > 0 && /*#__PURE__*/ _jsx(LicenseHistory, {
                                licenses: licensesData.licenses,
                                customerId: contract.CustomerId
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export default ContractCard;
